import { useEffect, useRef } from 'react';
import type { EditorProps } from '../types/editor-props';
import { VatSummaryForm } from '../../../forms/vat-summary/VatSummaryForm';
import { useVatRowEditor } from '../../../forms/vat-summary/hooks';
import type { VatSummaryFormData } from '../../../modals/EditVatSummaryModal';
import { Button } from '../../../../components';
import { useTranslation } from '../../../../providers/UIProvider';
import {
  useInvoiceCurrencyCode,
  useInvoiceRecomputeVatSummary,
  useInvoiceTotalAmountInclVat,
  useInvoiceTotalAmountNoVat,
  useInvoiceVatRateOptions,
  useVatSummaryCurrencyCode,
  useVatSummaryExchangeRate
} from '../../../../providers/InvoiceProvider';
import { computeVatSummaryTotals, toVatSummaryItems } from '../utils/vat-summary';

/** Inline editor for structured VAT summary values. */
export function VatSummaryInlineEditor({
  value,
  onChange,
  onCommit,
  onCancel,
  errors,
  onRegisterSave,
  onDraftChange
}: EditorProps) {
  const t = useTranslation('modules.modals.editVatSummary');
  const vatSummaryCurrencyCode = useVatSummaryCurrencyCode();
  const vatSummaryExchangeRate = useVatSummaryExchangeRate();
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  const vatRateOptions = useInvoiceVatRateOptions();
  const invoiceTotalAmountInclVat = useInvoiceTotalAmountInclVat();
  const invoiceTotalAmountNoVat = useInvoiceTotalAmountNoVat();
  const onRecomputeVatSummary = useInvoiceRecomputeVatSummary();

  const items = toVatSummaryItems(value);
  const rowTotals = computeVatSummaryTotals(items);
  const currentData = {
    totalAmount: invoiceTotalAmountInclVat ?? rowTotals.totalAmount,
    totalAmountNoVat: invoiceTotalAmountNoVat ?? rowTotals.totalAmountNoVat,
    vatSummary: rowTotals.vatSummary,
    vatSummaryCurrencyCode,
    vatSummaryExchangeRate
  };

  const handleSave = async (next: VatSummaryFormData) => {
    if (onCommit) {
      await onCommit(next);
    } else {
      onChange(next);
    }
  };

  const handleClose = () => {
    onCancel?.();
  };

  const editor = useVatRowEditor({
    open: true,
    currentData,
    vatRateOptions: vatRateOptions.length > 0 ? [...vatRateOptions] : undefined,
    onSave: handleSave,
    onClose: handleClose,
    errorMessage: t('errors.saveError'),
    recompute: onRecomputeVatSummary,
    rethrowOnError: !!onRegisterSave
  });

  const editorWithDraftChange = {
    ...editor,
    onCellChange: (...args: Parameters<typeof editor.onCellChange>) => {
      editor.onCellChange(...args);
      onDraftChange?.();
    },
    onCellBlur: (...args: Parameters<typeof editor.onCellBlur>) => {
      editor.onCellBlur(...args);
      onDraftChange?.();
    },
    setCellAmount: (...args: Parameters<typeof editor.setCellAmount>) => {
      editor.setCellAmount(...args);
      onDraftChange?.();
    },
    removeRow: (...args: Parameters<typeof editor.removeRow>) => {
      editor.removeRow(...args);
      onDraftChange?.();
    },
    addCustomRow: () => {
      editor.addCustomRow();
      onDraftChange?.();
    },
    setCurrencyCode: (...args: Parameters<typeof editor.setCurrencyCode>) => {
      editor.setCurrencyCode(...args);
      onDraftChange?.();
    },
    setRawExchangeRate: (...args: Parameters<typeof editor.setRawExchangeRate>) => {
      editor.setRawExchangeRate(...args);
      onDraftChange?.();
    },
    // Awaits the recompute so the draft is flagged dirty once the rows actually
    // change, not before the fetch resolves.
    fillFromLines: async () => {
      await editor.fillFromLines();
      onDraftChange?.();
    }
  };

  // Register editor.handleSave with the ScanEditor section save coordinator.
  const editorSaveRef = useRef(editor.handleSave);
  editorSaveRef.current = editor.handleSave;
  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => editorSaveRef.current());
    return () => onRegisterSave(null);
  }, [onRegisterSave]);

  return (
    <div className='flex flex-col gap-sm w-full' data-hover-field='vatSummary'>
      <VatSummaryForm
        editor={editorWithDraftChange}
        open={true}
        currentData={currentData}
        currencyCode={vatSummaryCurrencyCode ?? invoiceCurrencyCode}
        errors={errors}
      />
      {!onRegisterSave && (
        <div className='flex justify-end gap-sm'>
          <Button variant='ghost' size='sm' onClick={handleClose} disabled={editor.saving}>
            {t('actions.cancel')}
          </Button>
          <Button variant='primary' size='sm' onClick={editor.handleSave} disabled={editor.saving}>
            {editor.saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </div>
      )}
    </div>
  );
}

VatSummaryInlineEditor.displayName = 'VatSummaryInlineEditor';
